import { memo } from "react"
import { motion } from "motion/react"
import { Avatar } from "@/components/ui/Avatar"
import { Badge } from "@/components/ui/badge"
import {
  participantRoleLabels,
  participantPermissionLabels,
} from "@/constants/participant.constants"
import type { DemoParticipant } from "@/types/demo.types"
import { useDemoUsers } from "@/hooks/common/useDemoUsers"
import { getPermissionIcon } from "../../utils"

interface ParticipantItemProps {
  participant: DemoParticipant
}

export const ParticipantItem = memo(({ participant }: ParticipantItemProps) => {
  const { profilePictures } = useDemoUsers()
  const PermIcon = getPermissionIcon(participant.permission)
  const avatarSrc = participant.userId
    ? profilePictures[participant.userId as keyof typeof profilePictures]
    : participant.avatar

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex items-center gap-3"
    >
      <Avatar
        src={avatarSrc}
        fallback={participant.name.charAt(0)}
        className="size-7"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-foreground text-xs font-medium truncate">{participant.name}</p>
          {participant.status === "pending" && (
            <Badge variant="warning" size="sm" className="text-[9px] px-1 py-0">
              Pending
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <PermIcon className="size-2.5 text-muted-foreground" />
          <p className="text-muted-foreground text-[10px]">{participantRoleLabels[participant.role]}</p>
        </div>
      </div>
      <Badge variant="secondary" size="sm" className="text-[10px]">
        {participantPermissionLabels[participant.permission].replace(" Only", "").replace(" Access", "")}
      </Badge>
    </motion.div>
  )
})

ParticipantItem.displayName = "ParticipantItem"
